import React, { useEffect, useState, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { useHistory } from 'react-router-dom'
import _ from 'lodash'
import { isBefore } from 'date-fns'

import { getGlobalParams, getCustomIntl } from 'config/config'
import { getSpecificAttrValue, replaceSpecialChars } from 'utils'
import { updateBreadCrumb, displaySoftCloseMsg, updateHtmlTitleWithoutSuffix, updateMetaTag } from 'utils/interaction'
import { dispatchFundDetailPage, dispatchFundDetailEvent } from 'utils/tracking'
import { mergeDetailBy } from 'utils/merge'
import { updateUrlWithParams, updateFundDetailCanonicalUrl, removeCanonicalUrl } from 'utils/url'
import HomepageLevelMessage from '../../../atoms/HomepageLevelMessage'

import fundDetailRules from './rule'
import {
  getFundList,
  getFundShareClassGroup,
  getFundDetail,
  getFundOverview,
  getFundDividend,
  getFundDocument,
  getFundHoldings,
  getFundESG,
  getContentEsgFund,
  getFocusFundPDF,
  getContentApiData,
  getEsgDownloadData,
  getEsgDisclosureData,
  addToRecentView
} from '../services'

import PageLevelMessage from '../../../atoms/PageLevelMessage'
import Grid from '../../../atoms/FoundationGrid/Grid'
import Cell from '../../../atoms/FoundationGrid/Cell'
import FundDetail from '../../../templates/FundDetail'
import FundDetailOverviewTab from '../../../organisms/FundDetailOverviewTab'
import FundDetailDividendRecordsTab from '../../../organisms/FundDetailDividendRecordsTab'
import FundDetailPerformanceTab from './FundDetailPerformanceTab'
import FundDetailPortfolioTab from '../../../organisms/FundDetailPortfolioTab'
import FundDetailDocumentTab from '../../../organisms/FundDetailDocumentTab'
import FundDetailESGTab from '../../../organisms/FundDetailESGTab'

import './style.scss'

const TWFundDetail = () => {
  const defaultActiveIndex = 0
  const intl = getCustomIntl()
  const history = useHistory()
  const { countryConfig, terms, funds2CartJob, hasFundComparison, fundComparisonCart } = getGlobalParams()

  const [necessaryInfo, setNecessaryInfo] = useState()
  const [fundGeneralInfo, setFundGeneralInfo] = useState()
  const [fundFooterInfo, setFundFooterInfo] = useState()
  const [fundTabsInfo, setFundTabsInfo] = useState()
  const [isYoungFund, setIsYoungFund] = useState(false)
  const [performanceDisclaimerBaseDate, setPerformanceDisclaimerStatusByDate] = useState(false)
  const [currentFundsError, setCurrentFundsError] = useState()
  const [isFirstLoad, setIsFirstLoad] = useState(true)

  const necessaryInfoRef = useRef(necessaryInfo)

  const setNecessaryInfoRef = (x) => {
    necessaryInfoRef.current = x
    setNecessaryInfo(x)
  }

  const onHandleShareClassItemChange = ({ value }) => {
    addToRecentView({ scId: value })
    setFundGeneralInfo()
    setFundTabsInfo()
    setFundFooterInfo()
    setNecessaryInfoRef({
      ...necessaryInfoRef.current,
      shareClassId: value
    })
  }

  const updateUrl = (currentFund) => {
    const [oldShareclassName] = window.location.pathname
      .split('funds/')[1]
      .split('/')
      .filter((path) => path !== 0)

    const currentShareclassNameEn = replaceSpecialChars(
      getSpecificAttrValue({ originData: currentFund.shareclassName, attrValue: 'en-001' })
    )

    if (oldShareclassName !== currentShareclassNameEn) {
      const newUrl = _.replace(window.location.href, oldShareclassName, currentShareclassNameEn)
      updateUrlWithParams({ url: newUrl })
    }
  }

  const getFundGeneralInfo = async () => {
    setFundGeneralInfo()
    const { shareClassId, languageTags } = necessaryInfo
    const currentFund = await getFundDetail(shareClassId)
    if (!currentFund) {
      setCurrentFundsError(true)
    } else {
      await getContentApiData(shareClassId)
      const isContentEsgFund = getContentEsgFund()
      const focusFundPDF = getFocusFundPDF()
      const currentShareclassNameZh = getSpecificAttrValue({
        originData: currentFund.shareclassName,
        attrValue: 'zh-TW'
      })
      const currentFundCategory = getSpecificAttrValue({
        originData: currentFund.assetClass,
        dataPath: 'data',
        attrValue: 'zh-TW',
        targetAttr: 'name',
        valuePlaceholder: ''
      })

      const currentFundIsin = currentFund?.isin || ''
      const { purchaseLinks } = countryConfig

      const titleContent = intl.formatMessage({ id: 'fundDetail.meta.title' }, { fundName: currentShareclassNameZh, fundIsin: currentFundIsin, fundCategory: currentFundCategory})

      updateUrl(currentFund)
      updateHtmlTitleWithoutSuffix(titleContent)
      updateMetaTag([
        {
          attrName: 'name',
          attrContent: 'description',
          content: intl.formatMessage(
            { id: 'fundDetail.meta.description' },
            { fundName: currentShareclassNameZh, fundIsin: currentFundIsin }
          )
        },
        {
          attrName: 'name',
          attrContent: 'title',
          content: intl.formatMessage(
            { id: 'fundDetail.meta.title' },
            { fundName: currentShareclassNameZh, fundIsin: currentFundIsin, fundCategory: currentFundCategory }
          )
        }
      ])

      try {
        const generalInfo = mergeDetailBy(currentFund, fundDetailRules.generalInfo, terms)

        const currentShareClassItemObj = await getFundShareClassGroup(currentFund.fundId)

        if (!currentShareClassItemObj) {
          setCurrentFundsError(true)
        } else {
          const shareClassItems = currentShareClassItemObj
            ? _.get(currentShareClassItemObj, 'sharesWithScId')
                .sort((a, b) => {
                  const aClassShareNameEn = getSpecificAttrValue({
                    originData: a.classOfShares,
                    attrValue: languageTags.en
                  })
                  const bClassShareNameEn = getSpecificAttrValue({
                    originData: b.classOfShares,
                    attrValue: languageTags.en
                  })

                  return aClassShareNameEn.localeCompare(bClassShareNameEn)
                })
                .map((shareItem) => {
                  const classOfShareName = shareItem.classOfShares
                    ? getSpecificAttrValue({
                        originData: shareItem.classOfShares,
                        attrValue: languageTags.zh
                      })
                    : '-'
                  return { label: classOfShareName, value: shareItem.scId }
                })
            : []

          generalInfo.shareClassItemObj = {
            shareClassItem: shareClassItems,
            selectedShareClassItem: shareClassId,
            onHandleChange: onHandleShareClassItemChange
          }

          generalInfo.purchaseLink = generalInfo.isOnshoreFund ? purchaseLinks.onshore : purchaseLinks.offshore
          generalInfo.isEsgFund = isContentEsgFund
          generalInfo.focusFundPDF = focusFundPDF

          const showPeformanceDisclaimer = isBefore(new Date(generalInfo.launchDate), new Date(generalInfo.performanceStartDate))

          setIsYoungFund(generalInfo.isYoungFund)
          setFundGeneralInfo(generalInfo)
          setPerformanceDisclaimerStatusByDate(showPeformanceDisclaimer)
        }
      } catch (error) {
        setFundGeneralInfo()
      }
    }
  }

  const getFooterInfo = (selectedTab) => {
    const { msRatingDate, pastPerformanceDisclaimer } = fundGeneralInfo
    const footer = {
      id: `fil-fund-fundDetail-footer`,
      contents: [
        intl.formatMessage({ id: 'disclaimers.providerInfo' }),
        ...(msRatingDate ? [intl.formatMessage({ id: 'disclaimers.morningStarRating' }, { msRatingDate })] : []),
        ...(selectedTab === 'performance' && performanceDisclaimerBaseDate
          ? [intl.formatMessage({ id: 'disclaimers.performanceBase' })]
          : []),
        intl.formatMessage({ id: 'disclaimers.restDisclaimer' }),
        intl.formatMessage({ id: 'disclaimers.morningStarPerformance' }),
        intl.formatMessage({ id: 'disclaimers.riskRating' }),
        ...(pastPerformanceDisclaimer ? [pastPerformanceDisclaimer] : [])
      ]
    }

    setFundFooterInfo(footer)
  }

  const onTabChange = ({ tabUrlHash }) => {
    getFooterInfo(tabUrlHash)
    setTimeout(() => {
      updateFundDetailCanonicalUrl(tabUrlHash)
      const content = {
        content: {
          fundType: fundGeneralInfo?.fundType || '',
          investmentRegion: fundGeneralInfo?.investmentRegion || '',
          headFundName: fundGeneralInfo?.headFundName || '',
          displayName: fundGeneralInfo?.displayName || ''
        }
      }
      const product = {
        product: {
          isin: fundGeneralInfo?.isin || '',
          name: fundGeneralInfo?.shareClassEnglishName || ''
        }
      }
      dispatchFundDetailPage(content, product)
      if (isFirstLoad) {
        setIsFirstLoad(false)
      } else {
        dispatchFundDetailEvent('tab', tabUrlHash)
      }
    }, 0)
  }

  const getFundDetailOverviewTabInfo = async () => {
    const { investmentGoalContent } = fundGeneralInfo
    const formattedOverviewData = await getFundOverview(necessaryInfo.shareClassId)
    if (!formattedOverviewData) {
      return (
        <FundDetailOverviewTab>
          <Grid extraClasses="align-center">
            <Cell extraClasses="small-12 large-8">
              <PageLevelMessage id="fil-fund-detail__portfolio-tab-error-msg" content={intl.formatMessage({ id: 'dataErrorMessage' })} />
            </Cell>
          </Grid>
        </FundDetailOverviewTab>
      )
    }
    formattedOverviewData.fundGeneralInfo.investmentGoalContent = investmentGoalContent
    return <FundDetailOverviewTab {...formattedOverviewData} />
  }

  // 2nd Tab
  const getFundPerformanceTabInfo = async () => {
    const { shareClassId } = necessaryInfo
    const { pastPerformanceDisclaimer } = fundGeneralInfo

    return (
      <FundDetailPerformanceTab
        shareClassId={shareClassId}
        tableDisclaimer={!isYoungFund ? intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.tableDisclaimer' }) : ''}
        taCode={fundGeneralInfo.taCode}
        isYoungFund={isYoungFund}
        pastPerformanceDisclaimer={pastPerformanceDisclaimer}
      />
    )
  }

  // 3rd Tab
  const getFundPortfolioTabInfo = async () => {
    const { shareClassId } = necessaryInfo
    const { assetClassId, taCode } = fundGeneralInfo

    const holdingsType = assetClassId === 'BOF' ? 'type1' : assetClassId === 'FEF' ? 'type2' : 'type3'
    const fundDetailPortfolioTabInfo = await getFundHoldings(shareClassId, holdingsType, assetClassId, taCode === 'GFSTX')
    if (!fundDetailPortfolioTabInfo) {
      return (
        <FundDetailPortfolioTab gridExtraClasses="align-center">
          <Cell extraClasses="small-12 large-8">
            <PageLevelMessage id="fil-fund-detail__portfolio-tab-error-msg" content={intl.formatMessage({ id: 'dataErrorMessage' })} />
          </Cell>
        </FundDetailPortfolioTab>
      )
    }
    return (
      <FundDetailPortfolioTab
        fundDetailPortfolioTabInfo={fundDetailPortfolioTabInfo}
        shareClassId={shareClassId}
        holdingsType={holdingsType}
      />
    )
  }

  // 4th Tab
  const getFundDividendRecordsTabInfo = async () => {
    const { shareClassId } = necessaryInfo
    const { isOnshoreFund } = fundGeneralInfo
    const dividendData = await getFundDividend(shareClassId)
    if (!dividendData) {
      return (
        <FundDetailDividendRecordsTab id="fil-fund-detail-dividend-records-table">
          <Grid extraClasses="align-center">
            <Cell extraClasses="small-12 large-8">
              <PageLevelMessage id="fil-fund-detail__portfolio-tab-error-msg" content={intl.formatMessage({ id: 'dataErrorMessage' })} />
            </Cell>
          </Grid>
        </FundDetailDividendRecordsTab>
      )
    }
    const fundDetailDividendRecordsTabInfo = {
      fundDividendRecordsTable: {
        ...dividendData,
        tableData: {
          ...dividendData.tableData,
          withoutDataPlaceholder: intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.noResult' })
        },
        isOnshoreFund
      }
    }

    return (
      <FundDetailDividendRecordsTab
        id="fil-fund-detail-dividend-records-table"
        {...fundDetailDividendRecordsTabInfo}
        shareClassId={shareClassId}
        disclaimer={intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.disclaimer' })}
      />
    )
  }

  // 5th Tab
  /* temporarily remove this tab
  const getFundReportsTabInfo = () => {
    const { shareClassId } = necessaryInfo
    return <div>Fund Reports Tab - {shareClassId}</div>
  }
  */

  // 6th Tab
  const getRelatedDocsTabInfo = async () => {
    const { shareClassId } = necessaryInfo
    const documentData = await getFundDocument(shareClassId)
    if (!documentData) {
      return (
        <FundDetailDocumentTab id="fil-fund-detail-document-table">
          <Grid extraClasses="align-center">
            <Cell extraClasses="small-12 large-8">
              <PageLevelMessage id="fil-fund-detail__portfolio-tab-error-msg" content={intl.formatMessage({ id: 'dataErrorMessage' })} />
            </Cell>
          </Grid>
        </FundDetailDocumentTab>
      )
    }

    const fundDetailDocumentInfo = {
      fundDocumentTable: {
        ...documentData
      }
    }

    return <FundDetailDocumentTab id="fil-fund-detail-document-table" {...fundDetailDocumentInfo} shareClassId={shareClassId} />
  }

  // 7th Tab
  const geESGTabInfo = async () => {
    const { shareClassId } = necessaryInfo
    const { isOnshoreFund } = fundGeneralInfo

    const esgDownloadData = getEsgDownloadData()
    const esgDisclosureData = getEsgDisclosureData()

    try {
      const { fundESGTable, esgChartData, esgMetrics } = await getFundESG(shareClassId, isOnshoreFund)

      const fundDetailESGTabInfo = {
        fundESGTable,
        esgChartData,
        esgMetrics,
        esgDownloadData,
        esgDisclosureData
      }

      return <FundDetailESGTab id="fil-fund-detail-esg-tab" {...fundDetailESGTabInfo} />
    } catch (error) {
      return (
        <FundDetailESGTab id="fil-fund-detail-esg-tab">
          <Grid extraClasses="align-center">
            <Cell extraClasses="small-12 large-8">
              <PageLevelMessage id="fil-fund-detail__esg-tab-error-msg" content={intl.formatMessage({ id: 'dataErrorMessage' })} />
            </Cell>
          </Grid>
        </FundDetailESGTab>
      )
    }
  }

  const getTabsInfo = async () => {
    const { shareClassId } = necessaryInfo
    const getTabConfig = (tabsCondition = {}) => {
      const { withoutDividend, withoutHoldings, isEsgFund } = tabsCondition
      const allTabsConfig = {
        tabs: [
          {
            label: <FormattedMessage id="fundDetail.tabs.fundIntro" />,
            urlHash: 'overview'
          },
          {
            label: <FormattedMessage id="fundDetail.tabs.fundPerformance" />,
            urlHash: 'performance'
          },
          ...(!withoutHoldings
            ? [
                {
                  label: <FormattedMessage id="fundDetail.tabs.portfolio" />,
                  urlHash: 'portfolio'
                }
              ]
            : []),
          ...(!withoutDividend
            ? [
                {
                  label: <FormattedMessage id="fundDetail.tabs.dividendRecords" />,
                  urlHash: 'dividends'
                }
              ]
            : []),
          // {
          //   label: <FormattedMessage id="fundDetail.tabs.fundReports" />,
          //   urlHash: 'announcements'
          // },
          {
            label: <FormattedMessage id="fundDetail.tabs.relatedDocs" />,
            urlHash: 'documents'
          },
          ...(isEsgFund
            ? [
                {
                  label: <FormattedMessage id="fundDetail.tabs.esgTab.tabTitle" />,
                  urlHash: 'esg'
                }
              ]
            : [])
        ],
        panelsFunctions: [
          getFundDetailOverviewTabInfo,
          getFundPerformanceTabInfo,
          ...(!withoutHoldings ? [getFundPortfolioTabInfo] : []),
          ...(!withoutDividend ? [getFundDividendRecordsTabInfo] : []),
          // getFundReportsTabInfo,
          getRelatedDocsTabInfo,
          ...(isEsgFund ? [geESGTabInfo] : [])
        ],
        panels: [
          {
            element: null
          },
          {
            element: null
          },
          ...(!withoutHoldings ? [{ element: null }] : []),
          ...(!withoutDividend ? [{ element: null }] : []),
          // {
          //   element: null
          // },
          {
            element: null
          },
          ...(isEsgFund ? [{ element: null }] : [])
        ]
      }
      return allTabsConfig
    }

    let tabsConfig = {}

    if (fundGeneralInfo) {
      const { incomeTypeCode, assetClassId, isOnshoreFund, isEsgFund } = fundGeneralInfo
      const withoutDividend = incomeTypeCode && incomeTypeCode === 'ACC'
      const withoutHoldings = assetClassId === 'CAF' || isOnshoreFund
      tabsConfig = getTabConfig({ withoutDividend, withoutHoldings, isEsgFund })
    }

    const tabsInfo = {
      id: `fil-fund-${shareClassId}-tabsInfo`,
      fundDetailTabsConfig: tabsConfig,
      activeIndex: defaultActiveIndex,
      moreContent: intl.formatMessage({ id: 'fundDetail.tabs.moreContent' })
    }
    setFundTabsInfo(tabsInfo)
  }

  useEffect(async () => {
    if (necessaryInfo) {
      await getFundGeneralInfo()
    }
  }, [necessaryInfo])

  useEffect(() => {
    if (necessaryInfo && fundGeneralInfo) {
      if (fundGeneralInfo.isSoftClose) {
        displaySoftCloseMsg(true)
      }

      getTabsInfo()
      // getFooterInfo()
    }

    return () => {
      displaySoftCloseMsg(false)
    }
  }, [fundGeneralInfo])

  useEffect(async () => {
    const { baseUrl } = getGlobalParams()
    let currentBreadCrumbName = ''
    let currentShareClassId = ''
    const { languageTags } = countryConfig.common

    if (history.location.state) {
      // Get fund data with share class id in State
      currentBreadCrumbName = history.location.state.fundName
      currentShareClassId = history.location.state.shareClassId
    } else {
      const fullFundList = await getFundList()
      if (!fullFundList) {
        setCurrentFundsError(true)
      } else {
        // Get fund list at first and pick share class and English name up
        const [currentFundName] = history.location.pathname
          .split('funds/')[1]
          .split('/')
          .filter((path) => path !== 0)
        // const fullFundList = await getFundList()
        const specificFund = fullFundList.filter((fund) => {
          const formattedShareclassName = replaceSpecialChars(fund.shareClassName.en)
          return formattedShareclassName === currentFundName
        })

        if (specificFund.length === 0) {
          window.location.replace(`${baseUrl.split('/funds')[0]}/fund-not-found`)
          return
        }
        currentBreadCrumbName = specificFund[0].fundName.zh
        currentShareClassId = specificFund[0].shareClassId
      }
    }

    if (hasFundComparison) {
      const currentItems = sessionStorage.getItem(fundComparisonCart) ? JSON.parse(sessionStorage.getItem(fundComparisonCart)) : []
      funds2CartJob.funds2CartTrigger(currentItems)
    }

    addToRecentView({ scId: currentShareClassId })
    // Call Django function to update breadcrumb
    updateBreadCrumb(currentBreadCrumbName, true)
    setNecessaryInfoRef({
      languageTags,
      shareClassId: currentShareClassId
    })
  }, [])

  useEffect(() => {
    return () => {
      funds2CartJob?.funds2CartTrigger([])
      removeCanonicalUrl()
    }
  }, [])

  return (
    <>
      {currentFundsError ? (
        <HomepageLevelMessage type="fundDetail" />
      ) : (
        <FundDetail generalInfo={fundGeneralInfo} tabsInfo={fundTabsInfo} footerInfo={fundFooterInfo} onTabChange={onTabChange} />
      )}
    </>
  )
}

export default TWFundDetail
